/**
 * Сохранение рекламной атрибуции Яндекс.Директ (yclid) и UTM
 * для передачи при регистрации клуба и офлайн-конверсий.
 */

const STORAGE_KEY = "bs_ad_attribution";
const COOKIE_NAME = "bs_ad_attr";
const TTL_MS = 90 * 24 * 60 * 60 * 1000;

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

function getCookieDomain() {
  const base = (process.env.REACT_APP_BASE_DOMAIN || "").trim().toLowerCase();
  if (!base || base === "localhost") return "";
  return `;domain=.${base}`;
}

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}=([^;]*)`),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name, value, maxAgeSec) {
  if (typeof document === "undefined") return;
  const secure =
    typeof window !== "undefined" && window.location?.protocol === "https:"
      ? ";Secure"
      : "";
  document.cookie = `${name}=${encodeURIComponent(value)};path=/${getCookieDomain()};max-age=${maxAgeSec};SameSite=Lax${secure}`;
}

function parseStored(raw) {
  if (!raw) return null;
  try {
    const data = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!data || typeof data !== "object") return null;
    if (data.expiresAt && Date.now() > Number(data.expiresAt)) return null;
    return data;
  } catch {
    return null;
  }
}

function persist(data) {
  const payload = {
    ...data,
    expiresAt: Date.now() + TTL_MS,
    capturedAt: data.capturedAt || new Date().toISOString(),
  };
  const json = JSON.stringify(payload);
  try {
    localStorage.setItem(STORAGE_KEY, json);
  } catch {
    /* ignore quota / private mode */
  }
  writeCookie(COOKIE_NAME, json, Math.floor(TTL_MS / 1000));
  return payload;
}

/** Текущая сохранённая атрибуция (или null). */
export function getAdAttribution() {
  if (typeof window === "undefined") return null;
  const fromLs = parseStored(localStorage.getItem(STORAGE_KEY));
  if (fromLs) return fromLs;
  return parseStored(readCookie(COOKIE_NAME));
}

/**
 * Читает yclid/UTM из URL и сохраняет (first-touch: не перезаписывает yclid).
 * Вызывать при каждом заходе на SPA.
 */
export function captureAdAttributionFromUrl(search = window.location?.search) {
  if (typeof window === "undefined") return getAdAttribution();

  const params = new URLSearchParams(search || "");
  const yclid = String(params.get("yclid") || "").trim();
  const utms = {};
  for (const key of UTM_KEYS) {
    const v = String(params.get(key) || "").trim();
    if (v) utms[key] = v.slice(0, 256);
  }

  if (!yclid && !Object.keys(utms).length) {
    return getAdAttribution();
  }

  const existing = getAdAttribution() || {};
  const next = {
    ...existing,
    ...utms,
    // first-touch: новый yclid только если ещё не было
    yclid: existing.yclid || (yclid ? yclid.slice(0, 128) : undefined),
  };
  if (!next.yclid) delete next.yclid;
  return persist(next);
}

/** ClientID Метрики из cookie _ym_uid (если уже выставлен). */
export function readYmUidFromCookie() {
  const raw = readCookie("_ym_uid");
  if (!raw) return null;
  const id = String(raw).trim();
  return id ? id.slice(0, 64) : null;
}

/**
 * Поля для тела регистрации / создания клуба.
 */
export function getAdAttributionPayload() {
  const attr = getAdAttribution() || {};
  const ymClientId = readYmUidFromCookie();
  const payload = {};
  if (attr.yclid) payload.yclid = String(attr.yclid).slice(0, 128);
  if (ymClientId) payload.ym_client_id = ymClientId;
  for (const key of UTM_KEYS) {
    if (attr[key]) payload[key] = String(attr[key]).slice(0, 256);
  }
  return payload;
}
